import type { ApolloClient, TypedDocumentNode } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import Product from '@/clients/storefront/graphql/product.gql';
import ProductsPreview from '@/clients/storefront/graphql/productsPreview.gql';
import type {
  CartCreateMutation,
  CartCreateMutationVariables,
  CartLinesAddMutation,
  CartLinesAddMutationVariables,
  CartLinesRemoveMutation,
  CartLinesRemoveMutationVariables,
  CartLinesUpdateMutation,
  CartLinesUpdateMutationVariables,
  CartQuery,
  CartQueryVariables,
  ProductQuery,
  ProductQueryVariables,
  ProductsPreviewQuery,
  ProductsPreviewQueryVariables,
} from '@/types/graphql/storefront';
import CartLinesAdd from './graphql/addToCart.gql';
import Cart from './graphql/cart.gql';
import CartCreate from './graphql/createCart.gql';
import CartLinesRemove from './graphql/removeFromCart.gql';
import CartLinesUpdate from './graphql/updateCartItem.gql';
import type {
  CartItemInput,
  Cart as CartType,
  ProductPreviews as ProductPreviewsType,
  Product as ProductType,
  SHOPIFY_ENV,
} from './storefront.types';
import getStorefrontApolloClient from './storefront-apollo-client';

const toCartLineInput = ({
  merchandiseId,
  quantity,
  sellingPlanId,
  attributes,
}: CartItemInput) => ({
  merchandiseId,
  quantity,
  ...(sellingPlanId ? { sellingPlanId } : {}),
  ...(attributes?.length ? { attributes } : {}),
});

const asTypedDocument = <TData, TVariables>(
  document: DocumentNode
): TypedDocumentNode<TData, TVariables> =>
  document as TypedDocumentNode<TData, TVariables>;

const handleData = <TData>(
  gqlResponse:
    | ApolloClient.MutateResult<TData>
    | ApolloClient.QueryResult<TData>
): TData => {
  if (gqlResponse.data != null) {
    return gqlResponse.data;
  } else {
    if (gqlResponse.error != null) {
      const error = gqlResponse.error;
      console.error('Shopify Storefront API Exceptions:', error.message);
      throw new Error(`Storefront API returned exceptions: ${error.message}`);
    } else {
      console.error(
        'Shopify Storefront, unknown error no data or error object'
      );
      throw new Error('Storefront API returned unknown error');
    }
  }
};

export async function createCart(
  items: CartItemInput[],
  storefrontEnv: SHOPIFY_ENV
): Promise<CartType | undefined | null> {
  const variables: CartCreateMutationVariables = {
    input: {
      lines: items.map(toCartLineInput),
    },
  };

  const storefrontClient = getStorefrontApolloClient(storefrontEnv);

  const response = await storefrontClient.mutate({
    mutation: asTypedDocument<CartCreateMutation, CartCreateMutationVariables>(
      CartCreate
    ),
    variables,
  });

  const parsedResponse = handleData<CartCreateMutation>(response);
  return parsedResponse?.cartCreate?.cart;
}

export async function addToCart(
  cartId: string,
  items: CartItemInput[],
  storefrontEnv: SHOPIFY_ENV
): Promise<CartType | undefined | null> {
  const variables: CartLinesAddMutationVariables = {
    cartId,
    lines: items.map(toCartLineInput),
  };

  const storefrontClient = getStorefrontApolloClient(storefrontEnv);

  const response = await storefrontClient.mutate({
    mutation: asTypedDocument<
      CartLinesAddMutation,
      CartLinesAddMutationVariables
    >(CartLinesAdd),
    variables,
  });

  const parsedResponse = handleData(response);
  return parsedResponse?.cartLinesAdd?.cart;
}

export async function removeFromCart(
  cartId: string,
  lineItemIds: string[],
  storefrontEnv: SHOPIFY_ENV
): Promise<CartType | undefined | null> {
  const variables: CartLinesRemoveMutationVariables = {
    cartId,
    lineIds: lineItemIds,
  };
  const storefrontClient = getStorefrontApolloClient(storefrontEnv);
  const response = await storefrontClient.mutate({
    mutation: asTypedDocument<
      CartLinesRemoveMutation,
      CartLinesRemoveMutationVariables
    >(CartLinesRemove),
    variables,
  });

  const parsedResponse = handleData(response);
  return parsedResponse?.cartLinesRemove?.cart;
}

export async function updateCartLines(
  cartId: string,
  updates: { id: string; quantity: number }[],
  storefrontEnv: SHOPIFY_ENV
): Promise<CartType | undefined | null> {
  const variables = {
    cartId,
    lines: updates,
  };
  const storefrontClient = getStorefrontApolloClient(storefrontEnv);

  const response = await storefrontClient.mutate({
    mutation: asTypedDocument<
      CartLinesUpdateMutation,
      CartLinesUpdateMutationVariables
    >(CartLinesUpdate),
    variables,
  });

  const parsedResponse = handleData(response);
  return parsedResponse?.cartLinesUpdate?.cart;
}

export async function getCart(
  cartId: string,
  storefrontEnv: SHOPIFY_ENV
): Promise<CartType | undefined | null> {
  const variables: CartQueryVariables = { cartId };
  const storefrontClient = getStorefrontApolloClient(storefrontEnv);

  const response = await storefrontClient.query({
    query: asTypedDocument<CartQuery, CartQueryVariables>(Cart),
    variables,
  });

  const parsedResponse = handleData(response);
  return parsedResponse?.cart;
}

export async function getProductData(
  productId: string,
  storefrontEnv: SHOPIFY_ENV
): Promise<ProductType | undefined | null> {
  const variables: ProductQueryVariables = { productId };
  const storefrontClient = getStorefrontApolloClient(storefrontEnv);

  const response = await storefrontClient.query({
    query: asTypedDocument<ProductQuery, ProductQueryVariables>(Product),
    variables,
  });

  const parsedResponse = handleData(response);
  return parsedResponse?.product;
}

export async function getProductsPreviewData(
  productIds: string[],
  storefrontEnv: SHOPIFY_ENV
): Promise<ProductPreviewsType | undefined | null> {
  const storefrontClient = getStorefrontApolloClient(storefrontEnv);
  const response = await storefrontClient.query({
    query: asTypedDocument<ProductsPreviewQuery, ProductsPreviewQueryVariables>(
      ProductsPreview
    ),
    variables: { productIds },
  });
  const parsedResponse = handleData(response);
  return parsedResponse?.nodes;
}
